import { Component } from '@theme/component';

class BVSteroidMotion extends Component {
  #abort;
  #observer;
  #resizeObserver;
  #motion = matchMedia('(prefers-reduced-motion: reduce)');
  #finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  #regions = new Map();
  #visible = new Set();
  #counted = new WeakSet();
  #counters = new Map();
  #ripples = new Map();
  #frame = 0;
  #lastPaint = 0;
  #dirty = true;
  #pointer = { x: 0, y: 0, hero: null };

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    this.#observer = new IntersectionObserver(this.#intersect, { threshold: [0, 0.15] });
    this.#resizeObserver = new ResizeObserver(this.#resize);
    addEventListener('scroll', this.#scheduleParallax, { passive: true, signal });
    addEventListener('resize', this.#resize, { passive: true, signal });
    document.addEventListener('pointermove', this.#move, { passive: true, signal });
    document.addEventListener('pointerout', this.#leave, { passive: true, signal });
    document.addEventListener('pointerdown', this.#ripple, { passive: true, signal });
    document.addEventListener('click', this.#keyboardRipple, { signal });
    document.addEventListener('shopify:section:load', this.#initialize, { signal });
    document.addEventListener('shopify:section:unload', this.#prune, { signal });
    document.addEventListener('visibilitychange', this.#preferences, { signal });
    this.#motion.addEventListener('change', this.#preferences, { signal });
    this.#finePointer.addEventListener('change', this.#preferences, { signal });
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#finishCounters();
    for (const [animation, layer] of this.#ripples) { animation.cancel(); layer.remove(); }
    this.#ripples.clear();
    for (const state of this.#regions.values()) this.#resetRegion(state);
    this.#regions.clear();
    this.#visible.clear();
  }

  #initialize = () => {
    this.#prune();
    for (const region of document.querySelectorAll('.bv-cosmos, #MainContent .bv-future-hero, #MainContent .bv-story__visual, #MainContent .hero')) {
      if (this.#regions.has(region)) continue;
      const hero = region.matches('.bv-future-hero');
      const images = [];
      const layers = hero && this.dataset.multilayer === 'true'
        ? [['.bv-future-hero__orbit', 0.3], ['.bv-future-hero__grid', -0.2]]
          .map(([selector, depth]) => ({ element: region.querySelector(selector), depth })).filter(layer => layer.element)
        : [];
      for (const img of images) img.setAttribute('data-bv-parallax-image', '');
      for (const { element, depth } of layers) element.dataset.bvDepth = String(depth);
      const canvas = this.dataset.particles === 'true' && region.matches('.bv-cosmos') ? region.querySelector('.bv-steroid-particles') : null;
      const context = canvas?.getContext('2d');
      const state = { region, hero, images, layers, canvas, context, width: 0, height: 0, particles: [], hue: getComputedStyle(region).getPropertyValue('--bv-future-accent').trim() || '#77e3ff' };
      this.#regions.set(region, state);
      this.#observer.observe(region);
      if (canvas) { this.#resizeObserver.observe(region); this.#sizeCanvas(state); }
    }
    if (this.dataset.counter === 'true') {
      for (const counter of document.querySelectorAll('[data-bv-counter]')) {
        if (this.#counted.has(counter)) continue;
        this.#counted.add(counter);
        this.#observer.observe(counter);
      }
    }
    this.#scheduleParallax();
  };

  #prune = () => {
    for (const [region] of this.#regions) {
      if (region.isConnected) continue;
      this.#observer.unobserve(region);
      this.#resizeObserver.unobserve(region);
      this.#visible.delete(region);
      this.#regions.delete(region);
    }
  };

  #intersect = (entries) => {
    for (const entry of entries) {
      if (entry.target.hasAttribute('data-bv-counter')) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.15) continue;
        this.#observer.unobserve(entry.target);
        this.#startCounter(entry.target);
        continue;
      }
      if (entry.isIntersecting) this.#visible.add(entry.target);
      else this.#visible.delete(entry.target);
      entry.target.toggleAttribute('data-bv-steroid-visible', entry.isIntersecting);
    }
    this.#scheduleParallax();
  };

  #sizeCanvas(state) {
    if (!state.canvas || !state.context) return;
    const width = state.region.clientWidth;
    const height = state.region.clientHeight;
    if (!width || !height || width === state.width && height === state.height) return;
    state.width = width;
    state.height = height;
    const ratio = Math.min(devicePixelRatio || 1, 2, 5120 / width);
    state.canvas.width = Math.round(width * ratio);
    state.canvas.height = Math.round(height * ratio);
    state.context.setTransform(ratio, 0, 0, ratio, 0, 0);
    const count = width < 750 ? 440 : Math.min(1200, Math.round(width * .8));
    let seed = 19841;
    const random = () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };
    state.scroll = window.scrollY;
    state.particles = Array.from({ length: count }, (_, index) => ({
      x: random() * width, y: random() * (height + 160) - 80,
      radius: 0.35 + random() * (index < 64 ? 1.5 : 0.65),
      phase: random() * Math.PI * 2,
      depth: index < 64 ? 0.12 : 0.025 + (index % 3) * 0.025
    }));
    state.dust = Array.from({ length: width < 750 ? 220 : 560 }, () => ({
      x: random() * width, y: random() * (height + 160) - 80,
      radius: 0.35 + random() * 0.8, phase: random() * 6.28, depth: 0.18 + random() * 0.25
    }));
    state.moon = state.region.querySelector('.bv-cosmos__moon');
    state.scenes = [...state.region.querySelectorAll('[data-space-scene]')];
    // Stylized recognizable asterisms; a decorative sky, not a sky-position chart.
    state.constellations = [
      {name:'ORION',x:.08,y:.28,size:190,depth:.09,points:[[.28,0],[.04,.22],[.62,.15],[.28,.5],[.39,.46],[.5,.42],[.12,.94],[.7,.84]],edges:[[0,1],[0,2],[1,3],[3,4],[4,5],[5,2],[3,6],[5,7],[6,7]]},
      {name:'CASSIOPEIA',x:.57,y:.16,size:230,depth:.06,points:[[0,.12],[.22,.52],[.47,.03],[.7,.44],[1,.12]],edges:[[0,1],[1,2],[2,3],[3,4]]},
      {name:'URSA MAJOR · BIG DIPPER',x:.58,y:.7,size:250,depth:.12,points:[[0,.52],[.17,.29],[.38,.22],[.57,.32],[.87,.24],[.94,.56],[.64,.64]],edges:[[0,1],[1,2],[2,3],[3,4],[4,5],[5,6],[6,3]]},
      {name:'LYRA',x:.18,y:.84,size:145,depth:.08,points:[[.12,0],[.42,.3],[.82,.36],[.61,.86],[.2,.8]],edges:[[0,1],[1,2],[2,3],[3,4],[4,1]]}
    ];
    this.#drawParticles(state, 0);
  }

  #resize = () => {
    for (const state of this.#regions.values()) this.#sizeCanvas(state);
    this.#scheduleParallax();
  };

  #move = (event) => {
    if (this.#motion.matches || !this.#finePointer.matches || event.pointerType === 'touch' || !(event.target instanceof Element)) return;
    const hero = event.target.closest('.bv-future-hero');
    if (!hero || !this.#regions.has(hero)) return;
    this.#pointer = { hero, x: event.clientX, y: event.clientY };
    this.#scheduleParallax();
  };

  #leave = (event) => {
    const hero = this.#pointer.hero;
    if (!hero || event.relatedTarget instanceof Node && hero.contains(event.relatedTarget)) return;
    this.#pointer = { hero: null, x: 0, y: 0 };
    this.#scheduleParallax();
  };

  #scheduleParallax = () => {
    this.#dirty = true;
    this.#schedule();
  };

  #schedule() {
    if (this.#frame || document.hidden || this.#motion.matches || window.Shopify?.designMode) return;
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #tick = (time) => {
    this.#frame = 0;
    if (document.hidden || this.#motion.matches || !this.isConnected) return;
    if (this.#dirty) { this.#renderParallax(); this.#dirty = false; }
    let particlesActive = false;
    for (const region of this.#visible) {
      const state = this.#regions.get(region);
      if (!state?.context) continue;
      particlesActive = true;
      if (time - this.#lastPaint >= 16) this.#drawParticles(state, Math.min(64, time - this.#lastPaint));
    }
    if (time - this.#lastPaint >= 16) this.#lastPaint = time;
    for (const [element, state] of this.#counters) {
      const progress = Math.min(1, (time - state.started) / 1300);
      const value = Math.round(state.end * (1 - Math.pow(1 - progress, 3)));
      if (value !== state.value) { element.textContent = String(value); state.value = value; }
      if (progress === 1 || !element.isConnected) {
        element.textContent = state.original;
        element.dataset.bvCounterState = 'complete';
        this.#counters.delete(element);
      }
    }
    if (particlesActive || this.#counters.size) this.#schedule();
  };

  #renderParallax() {
    const mobile = innerWidth < 750;
    const strength = Math.max(1, Math.min(5, Number(this.dataset.strength) || 3)) / 3;
    const measurements = [...this.#visible].map(region => ({ state: this.#regions.get(region), rect: region.getBoundingClientRect() }));
    for (const { state, rect } of measurements) {
      if (!state || !rect.height) continue;
      const scroll = Math.max(-1, Math.min(1, (innerHeight / 2 - rect.top - rect.height / 2) / ((innerHeight + rect.height) / 2)));
      const pointerHere = this.#pointer.hero === state.region && this.#finePointer.matches;
      const px = pointerHere ? Math.max(-1, Math.min(1, (this.#pointer.x - rect.left) / rect.width * 2 - 1)) : 0;
      const py = pointerHere ? Math.max(-1, Math.min(1, (this.#pointer.y - rect.top) / rect.height * 2 - 1)) : 0;
      for (const image of state.images) image.style.setProperty('--bv-parallax-y', `${(scroll * (mobile ? 12 : 30) * strength).toFixed(2)}px`);
      for (const { element, depth } of state.layers) {
        element.style.setProperty('--bv-depth-x', `${(px * 24 * depth * strength).toFixed(2)}px`);
        element.style.setProperty('--bv-depth-y', `${((scroll * (mobile ? 14 : 42) + py * 18) * depth * strength).toFixed(2)}px`);
      }
    }
  }

  #drawParticles(state, delta) {
    const { context:c, width:w, height:h }=state;
    state.elapsed=(state.elapsed||0)+delta;
    const t=state.elapsed/1000;
    const target=this.#motion.matches?0:window.scrollY;
    state.scroll+=(target-state.scroll)*(delta?1-Math.exp(-delta/110):1);
    const scroll=this.dataset.parallax==='true'?state.scroll:0;
    const total=Math.max(1,document.documentElement.scrollHeight-h);
    const progress=Math.min(1,Math.max(0,scroll/total));
    const smooth=(v)=>{const n=Math.max(0,Math.min(1,v));return n*n*n*(n*(n*6-15)+10);};
    const wrap=(y,pad=80)=>((y+pad)%(h+pad*2)+(h+pad*2))%(h+pad*2)-pad;
    c.clearRect(0,0,w,h);
    // Low contrast interstellar haze sits behind the individual dust particles.
    c.globalAlpha=1;
    for(let i=0;i<5;i++) {
      const x=w*(.12+i*.22), y=h*(.82-i*.12)-Math.min(h*.2,scroll*.018);
      const radius=Math.max(w,h)*.26;
      const haze=c.createRadialGradient(x,y,0,x,y,radius);
      haze.addColorStop(0,'rgba(91,108,140,.025)');
      haze.addColorStop(.4,'rgba(62,77,108,.012)');
      haze.addColorStop(1,'rgba(12,19,35,0)');
      c.fillStyle=haze;c.fillRect(x-radius,y-radius,radius*2,radius*2);
    }
    const points=state.particles.map(p=>{const lens=1+Math.sin(progress*Math.PI)*p.depth*.7;return {x:w*.5+(p.x-w*.5)*lens,y:wrap(p.y-scroll*p.depth),r:p.radius*lens,a:.48+.26*Math.sin(t*.5+p.phase)};});
    for(const [i,p] of points.entries()){
      c.globalAlpha=p.a;
      c.fillStyle=i%7===0?'#e4cfb4':'#d1e4f5';
      c.beginPath();c.arc(p.x,p.y,p.r,0,Math.PI*2);c.fill();
      if(i<64&&i%8===0){
        const glow=c.createRadialGradient(p.x,p.y,0,p.x,p.y,9);
        glow.addColorStop(0,'#c3e4ff88');glow.addColorStop(1,'#9ed3ff00');
        c.fillStyle=glow;c.fillRect(p.x-9,p.y-9,18,18);
        c.strokeStyle='#cee8ff77';c.lineWidth=.5;c.beginPath();c.moveTo(p.x-4,p.y);c.lineTo(p.x+4,p.y);c.moveTo(p.x,p.y-4);c.lineTo(p.x,p.y+4);c.stroke();
      }
    }
    for(const p of state.dust){
      const x=p.x+Math.sin(t*.08+p.phase)*10;
      const y=wrap(p.y-scroll*p.depth-t*1.4);
      const band=Math.exp(-Math.pow((x/w-.66-y/h*.2)*3,2));
      c.globalAlpha=(.12+band*.3)*(.65+.35*Math.sin(t*.3+p.phase));
      c.fillStyle='#a7bedf';c.beginPath();c.arc(x,y,p.radius,0,Math.PI*2);c.fill();
    }
    this.#drawConstellations(state, scroll, t, progress);
    // Fine foreground dust curls into the final gravitational encounter.
    const gravity=smooth((progress-.76)/.24);
    if(gravity>0) {
      const cx=w*.73,cy=h*.36;
      c.save();c.translate(cx,cy);c.rotate(-.45);
      for(let i=0;i<90;i++) {
        const angle=i*2.39996+t*.018+progress*2;
        const radius=(35+(i%30)*6)*Math.min(w/900,1.5);
        const x=Math.cos(angle)*radius*1.8,y=Math.sin(angle)*radius*.42;
        c.globalAlpha=gravity*(.08+(i%5)*.03);
        c.strokeStyle=i%7===0?'#d3c5aa':'#d6e4f2';c.lineWidth=.6;
        c.beginPath();c.moveTo(x,y);
        c.lineTo(Math.cos(angle+.07)*radius*1.8,Math.sin(angle+.07)*radius*.42);c.stroke();
      }
      c.restore();c.globalAlpha=1;
    }
    // Quintic easing gives every chapter a soft acceleration and deceleration.
    const rawChapter=progress*Math.max(1,state.scenes.length-1);
    const chapter=Math.floor(rawChapter)+smooth(rawChapter%1);
    for(const [i,scene] of (state.scenes || []).entries()) {
      const weight=Math.max(0,1-Math.abs(chapter-i));
      scene.style.opacity=(weight*(i===0?.56:i===1?.5:i===6?.78:i===5?.34:.42)).toFixed(4);
      if(weight>0) {
        const local=rawChapter-i;
        scene.style.transform=`translate3d(${local*-w*.025}px, ${local*-h*.085}px, 0) scale(${1.08+progress*.12})`;
      }
    }
    if(state.moon) {
      // The first celestial encounter is paced in viewports, independent of page length.
      const encounter=Math.min(h*3.6,Math.max(h*1.5,total*.65));
      const phase=Math.min(1,scroll/encounter);
      const approach=smooth(phase/.72);
      const depart=smooth((phase-.66)/.34);
      const mobile=w<750;
      const zoom=1+approach*(mobile?.52:.82);
      const x=-w*.065*approach+w*.24*depart;
      const y=-h*.08*approach-h*.44*depart;
      state.moon.style.transform=`translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${zoom.toFixed(5)}) rotate(${(-2.2*approach).toFixed(3)}deg)`;
      state.moon.style.opacity=(.92*(1-depart)).toFixed(4);
      state.moon.style.setProperty('--bv-network-opacity',(smooth((phase-.08)/.38)*.64*(1-depart)).toFixed(4));
      state.moon.style.setProperty('--bv-network-turn',`${(approach*12).toFixed(3)}deg`);
    }
    c.globalAlpha=1;
  }

  #drawConstellations(state, scroll, time, progress) {
    const {context:c,width:w,height:h}=state;
    for(const [index,group] of state.constellations.entries()) {
      const size=group.size*(w<750?.66:Math.min(1.4,w/1280));
      const x=group.x*w, span=h+size*1.6;
      const y=((group.y*h-scroll*group.depth+size)%span+span)%span-size;
      const pts=group.points.map(([px,py])=>[x+px*size,y+py*size]);
      c.globalAlpha=1;c.lineWidth=.8;c.strokeStyle='rgba(168,206,237,.28)';
      const reveal=this.#motion.matches?1:Math.min(1,.42+progress*2+index*.08);
      for(const [a,b] of group.edges) {
        const p=pts[a],q=pts[b];
        c.strokeStyle='rgba(168,206,237,.15)';c.beginPath();c.moveTo(...p);c.lineTo(...q);c.stroke();
        c.strokeStyle='rgba(168,206,237,.28)';c.beginPath();c.moveTo(...p);
        c.lineTo(p[0]+(q[0]-p[0])*reveal,p[1]+(q[1]-p[1])*reveal);c.stroke();
      }
      for(const [i,[px,py]] of pts.entries()) {
        const radius=i===0?2.1:1.35;
        const glow=c.createRadialGradient(px,py,0,px,py,11);
        glow.addColorStop(0,'rgba(188,222,255,.55)');glow.addColorStop(1,'rgba(150,200,255,0)');
        c.globalAlpha=.75+.2*Math.sin(time*.4+i);c.fillStyle=glow;c.fillRect(px-11,py-11,22,22);
        c.fillStyle='#e5f2ff';c.beginPath();c.arc(px,py,radius,0,Math.PI*2);c.fill();
      }
      c.globalAlpha=.32;c.font='9px monospace';c.fillStyle='#c2d8ed';
      c.fillText(group.name,x,y+size*1.08);
    }
    c.globalAlpha=1;
  }

  #startCounter(element) {
    const end = Number(element.dataset.bvCounter);
    if (!Number.isFinite(end) || end < 0 || this.#motion.matches || window.Shopify?.designMode) return;
    this.#counters.set(element, { end, original: element.textContent, started: performance.now(), value: 0 });
    element.textContent = '0';
    element.dataset.bvCounterState = 'running';
    this.#schedule();
  }

  #finishCounters() {
    for (const [element, state] of this.#counters) { element.textContent = state.original; element.dataset.bvCounterState = 'complete'; }
    this.#counters.clear();
  }

  #keyboardRipple = (event) => { if (event.detail === 0) this.#ripple(event); };

  #ripple = (event) => {
    if (this.dataset.ripple !== 'true' || this.#motion.matches || window.Shopify?.designMode || !(event.target instanceof Element)) return;
    if (event.type === 'pointerdown' && event.button !== 0) return;
    const button = event.target.closest('.button, .button-secondary, button.add-to-cart-button, .bv-future-hero__scroll');
    if (!button || button.matches(':disabled, [aria-disabled="true"]')) return;
    const rect = button.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const size = Math.hypot(rect.width, rect.height) * 2;
    const keyboard = event.type === 'click';
    const x = keyboard ? rect.width / 2 : event.clientX - rect.left;
    const y = keyboard ? rect.height / 2 : event.clientY - rect.top;
    const layer = document.createElement('span');
    layer.className = 'bv-steroid-ripple-layer';
    layer.setAttribute('aria-hidden', 'true');
    const circle = document.createElement('span');
    circle.className = 'bv-steroid-ripple';
    circle.style.cssText = `width:${size}px;height:${size}px;left:${x - size / 2}px;top:${y - size / 2}px;`;
    layer.append(circle); button.append(layer);
    const animation = circle.animate([{ transform: 'scale(0)', opacity: 1 }, { transform: 'scale(1)', opacity: 0 }], { duration: 650, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' });
    this.#ripples.set(animation, layer);
    animation.finished.catch(() => {}).finally(() => { layer.remove(); this.#ripples.delete(animation); });
  };

  #resetRegion(state) {
    if (state.moon) state.moon.style.transform = 'none';
    if (state.context && this.#motion.matches) this.#drawParticles(state, 0);
    for (const image of state.images) image.style.removeProperty('--bv-parallax-y');
    for (const { element } of state.layers) { element.style.removeProperty('--bv-depth-x'); element.style.removeProperty('--bv-depth-y'); }
  }

  #preferences = () => {
    cancelAnimationFrame(this.#frame); this.#frame = 0; this.#lastPaint = 0;
    this.#finishCounters();
    this.#pointer = { hero: null, x: 0, y: 0 };
    for (const [animation, layer] of this.#ripples) { animation.cancel(); layer.remove(); }
    this.#ripples.clear();
    if (this.#motion.matches) for (const state of this.#regions.values()) this.#resetRegion(state);
    this.#scheduleParallax();
  };
}

if (!customElements.get('bv-steroid-motion')) customElements.define('bv-steroid-motion', BVSteroidMotion);




